/* eslint-disable sonarjs/cognitive-complexity, complexity -- bidirectional API<->grid field mapping is inherently long and branchy; splitting it would fragment the 1:1 field correspondence without reducing real complexity */
/**
 * Transform functions for Short-Term Disability (STD) benefits
 *
 * Handles bi-directional transformation between:
 * - API format (nested objects) - what the backend expects/returns
 * - Grid format (flat fields) - what the AgGrid component uses
 */

import {
  DefinitionOfEarningsEnum,
  RfpStdBenefitDurationTypeEnum,
} from "@platform/openapi-types";

import {
  buildConditionalTypeAmount,
  cleanupNotDisclosedBenefits,
  isValidAmount,
  shouldIncludeBenefitInApi,
  transformPreExistingConditionFromApi,
  transformPreExistingConditionToApi,
} from "../shared/builders";

import { STD_VALID_FIELDS } from "./validFields";

import type {
  StdBenefitApi,
  StdBenefitGrid,
  StdExtendedBenefitApi,
} from "./types";

// Types that carry an averageMonth (variable compensation needing an averaging period)
const DOE_TYPES_WITH_AVERAGE_MONTH = new Set<string>([
  DefinitionOfEarningsEnum.salaryPlusCommissions,
  DefinitionOfEarningsEnum.salaryPlusBonus,
  DefinitionOfEarningsEnum.salaryPlusBonusPlusCommissions,
]);

/**
 * Transforms STD benefits from grid format to API format
 *
 * @param gridBenefits - Benefits in flat grid format
 * @returns Benefits in nested API format with objectType and valid fields filtered
 */
export const transformStdBenefitsToApi = (
  gridBenefits: Partial<StdBenefitGrid>,
): StdBenefitApi => {
  const transformed: Partial<StdBenefitApi> = {};

  // Transform BenefitAmount (type + amount + minimum)
  if (
    shouldIncludeBenefitInApi(gridBenefits.benefitAmountType) &&
    (isValidAmount(gridBenefits.benefitAmountAmount) ||
      isValidAmount(gridBenefits.benefitAmountMinimum))
  ) {
    transformed.benefitAmount = {
      type: gridBenefits.benefitAmountType,
      ...(isValidAmount(gridBenefits.benefitAmountAmount)
        ? { amount: gridBenefits.benefitAmountAmount }
        : {}),
      ...(isValidAmount(gridBenefits.benefitAmountMinimum)
        ? { minimum: gridBenefits.benefitAmountMinimum }
        : {}),
    };
  }

  // Transform MaximumAmount (type + dollarAmount + percent - different cell types)
  if (shouldIncludeBenefitInApi(gridBenefits.maximumAmountType)) {
    transformed.maximumAmount = {
      type: gridBenefits.maximumAmountType,
      ...(isValidAmount(gridBenefits.maximumAmountDollarAmount)
        ? { dollarAmount: gridBenefits.maximumAmountDollarAmount }
        : {}),
      ...(isValidAmount(gridBenefits.maximumAmountPercent)
        ? { percent: gridBenefits.maximumAmountPercent }
        : {}),
    };
  }

  // Simple field mappings
  if (shouldIncludeBenefitInApi(gridBenefits.definitionOfDisability))
    transformed.definitionOfDisability = gridBenefits.definitionOfDisability;

  // Transform DefinitionOfEarnings (type + averageMonth)
  // averageMonth is optional and only sent for variable-compensation types; fixed-point types (salary, k1, etc.) are type-only
  if (shouldIncludeBenefitInApi(gridBenefits.definitionOfEarningsType)) {
    const supportsAverageMonth = DOE_TYPES_WITH_AVERAGE_MONTH.has(
      gridBenefits.definitionOfEarningsType ?? "",
    );
    const result = buildConditionalTypeAmount(
      gridBenefits.definitionOfEarningsType,
      gridBenefits.definitionOfEarningsAverageMonths,
      "averageMonth",
      false,
      supportsAverageMonth,
    );
    if (result) transformed.definitionOfEarnings = result;
  }

  if (shouldIncludeBenefitInApi(gridBenefits.employerContribution))
    transformed.employerContribution = gridBenefits.employerContribution;

  // Transform EliminationPeriodAccident (type + amount)
  // amount only applies to the "days" type; other types (notIncluded, notDisclosed,
  // notOffered) are type-only and must not send a stale amount
  if (shouldIncludeBenefitInApi(gridBenefits.eliminationPeriodAccidentType)) {
    transformed.eliminationPeriodAccident = {
      type: gridBenefits.eliminationPeriodAccidentType,
    };
    if (
      gridBenefits.eliminationPeriodAccidentType === "days" &&
      isValidAmount(gridBenefits.eliminationPeriodAccidentDays)
    ) {
      transformed.eliminationPeriodAccident.amount =
        gridBenefits.eliminationPeriodAccidentDays;
    }
  }

  // Transform EliminationPeriodIllness (type + amount)
  // amount only applies to the "days" type; other types (notIncluded, notDisclosed,
  // notOffered) are type-only and must not send a stale amount
  if (shouldIncludeBenefitInApi(gridBenefits.eliminationPeriodIllnessType)) {
    transformed.eliminationPeriodIllness = {
      type: gridBenefits.eliminationPeriodIllnessType,
    };
    if (
      gridBenefits.eliminationPeriodIllnessType === "days" &&
      isValidAmount(gridBenefits.eliminationPeriodIllnessDays)
    ) {
      transformed.eliminationPeriodIllness.amount =
        gridBenefits.eliminationPeriodIllnessDays;
    }
  }

  // Transform BenefitDuration (type + amount)
  // amount required only for days/weeks; other types (notIncluded, notDisclosed, notOffered)
  // are type-only and must not send a stale amount
  if (shouldIncludeBenefitInApi(gridBenefits.benefitDurationType)) {
    const benefitDurationType = gridBenefits.benefitDurationType;
    const amountRequired =
      benefitDurationType === RfpStdBenefitDurationTypeEnum.days ||
      benefitDurationType === RfpStdBenefitDurationTypeEnum.weeks;
    const result = buildConditionalTypeAmount(
      benefitDurationType,
      gridBenefits.benefitDurationAmount,
      "amount",
      amountRequired,
    );
    if (result) transformed.benefitDuration = result;
  }

  // Transform PreExistingCondition (type + monthsDetail string) - using builder
  if (shouldIncludeBenefitInApi(gridBenefits.preExistingConditionType)) {
    transformed.preExistingCondition = transformPreExistingConditionToApi(
      gridBenefits.preExistingConditionType,
      gridBenefits.preExistingConditionMonthsDetail,
      "none",
      "01/01",
    ) as unknown as StdBenefitApi["preExistingCondition"];
  }

  if (shouldIncludeBenefitInApi(gridBenefits.ficaMatch))
    transformed.ficaMatch = gridBenefits.ficaMatch;
  if (shouldIncludeBenefitInApi(gridBenefits.billableType))
    transformed.billableType = gridBenefits.billableType;

  // Extended (non-v5) fields — CD-2068. Sent as a full object (a full-replace
  // PATCH, like the rest of `benefits`) whenever at least one extended field
  // is set, so an edit to any other field on the plan doesn't drop the
  // extended values already on it. Omitted entirely when every extended
  // field is unset (undefined or ""), so an edit that never touches these
  // fields doesn't create an empty extended row on the backend.
  const extended = {
    eligibilityHours: gridBenefits.eligibilityHours,
    minimumBenefitType: gridBenefits.minimumBenefitType,
    minimumBenefitAmount: gridBenefits.minimumBenefitAmount,
    firstDayHospital: gridBenefits.firstDayHospital,
    partialDisabilityFormula: gridBenefits.partialDisabilityFormula,
    partialDisability: gridBenefits.partialDisability,
    earningsTest: gridBenefits.earningsTest,
    portability: gridBenefits.portability,
    rehabilitativeIncentiveBenefit: gridBenefits.rehabilitativeIncentiveBenefit,
    recurrentDisability: gridBenefits.recurrentDisability,
    w2Prep: gridBenefits.w2Prep,
    claimsIntake: gridBenefits.claimsIntake,
    statutoryDiIntegration: gridBenefits.statutoryDiIntegration,
    pflIntegration: gridBenefits.pflIntegration,
  };
  if (
    Object.values(extended).some((value) => value !== undefined && value !== "")
  ) {
    transformed.extended = extended;
  }

  // Add objectType and filter to valid fields
  const withObjectType: StdBenefitApi = {
    ...transformed,
    objectType: "std",
  };

  // Filter to only valid STD fields
  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(withObjectType)) {
    if (STD_VALID_FIELDS.has(key)) {
      filtered[key] = value;
    }
  }

  return cleanupNotDisclosedBenefits<unknown>(filtered) as StdBenefitApi;
};

/**
 * Transforms STD benefits from API format to grid format
 *
 * @param apiBenefits - Benefits in nested API format
 * @returns Benefits in flat grid format
 */
export const transformStdBenefitsFromApi = (
  apiBenefits: Partial<StdBenefitApi>,
): StdBenefitGrid => {
  const transformed: Partial<StdBenefitGrid> = { ...apiBenefits };

  // Transform BenefitAmount from API object to grid fields
  if (
    apiBenefits.benefitAmount &&
    typeof apiBenefits.benefitAmount === "object"
  ) {
    transformed.benefitAmountType = apiBenefits.benefitAmount.type;
    transformed.benefitAmountAmount = apiBenefits.benefitAmount.amount;
    transformed.benefitAmountMinimum = apiBenefits.benefitAmount.minimum;

    // Remove the nested API field
    delete (transformed as Record<string, unknown>)["benefitAmount"];
  }

  // Transform MaximumAmount from API object to grid fields
  if (
    apiBenefits.maximumAmount &&
    typeof apiBenefits.maximumAmount === "object"
  ) {
    transformed.maximumAmountType = apiBenefits.maximumAmount.type;
    transformed.maximumAmountDollarAmount =
      apiBenefits.maximumAmount.dollarAmount;
    transformed.maximumAmountPercent = apiBenefits.maximumAmount.percent;

    // Remove the nested API field
    delete (transformed as Record<string, unknown>)["maximumAmount"];
  }

  // Transform DefinitionOfEarnings from API object to grid fields
  if (
    apiBenefits.definitionOfEarnings &&
    typeof apiBenefits.definitionOfEarnings === "object"
  ) {
    transformed.definitionOfEarningsType =
      apiBenefits.definitionOfEarnings.type;
    transformed.definitionOfEarningsAverageMonths =
      apiBenefits.definitionOfEarnings.averageMonth;

    // Remove the nested API field
    delete (transformed as Record<string, unknown>)["definitionOfEarnings"];
  }

  // Transform EliminationPeriodAccident from API object to grid fields
  if (
    apiBenefits.eliminationPeriodAccident &&
    typeof apiBenefits.eliminationPeriodAccident === "object"
  ) {
    transformed.eliminationPeriodAccidentType =
      apiBenefits.eliminationPeriodAccident.type;
    transformed.eliminationPeriodAccidentDays =
      apiBenefits.eliminationPeriodAccident.amount;

    // Remove the nested API field
    delete (transformed as Record<string, unknown>)[
      "eliminationPeriodAccident"
    ];
  }

  // Transform EliminationPeriodIllness from API object to grid fields
  if (
    apiBenefits.eliminationPeriodIllness &&
    typeof apiBenefits.eliminationPeriodIllness === "object"
  ) {
    transformed.eliminationPeriodIllnessType =
      apiBenefits.eliminationPeriodIllness.type;
    transformed.eliminationPeriodIllnessDays =
      apiBenefits.eliminationPeriodIllness.amount;

    // Remove the nested API field
    delete (transformed as Record<string, unknown>)["eliminationPeriodIllness"];
  }

  // Transform BenefitDuration from API object to grid fields
  if (
    apiBenefits.benefitDuration &&
    typeof apiBenefits.benefitDuration === "object"
  ) {
    transformed.benefitDurationType = apiBenefits.benefitDuration.type;
    transformed.benefitDurationAmount = apiBenefits.benefitDuration.amount;

    // Remove the nested API field
    delete (transformed as Record<string, unknown>)["benefitDuration"];
  }

  // Transform PreExistingCondition from API object to grid fields
  if (
    apiBenefits.preExistingCondition &&
    typeof apiBenefits.preExistingCondition === "object"
  ) {
    Object.assign(
      transformed,
      transformPreExistingConditionFromApi(apiBenefits.preExistingCondition),
    );

    // Remove the nested API field
    delete (transformed as Record<string, unknown>)["preExistingCondition"];
  }

  // Extended (non-v5) fields — CD-2068
  Object.assign(transformed, transformStdExtendedFromApi(apiBenefits.extended));
  delete (transformed as Record<string, unknown>)["extended"];

  return transformed as StdBenefitGrid;
};

type StdExtendedGridFields = Pick<
  StdBenefitGrid,
  | "eligibilityHours"
  | "minimumBenefitType"
  | "minimumBenefitAmount"
  | "firstDayHospital"
  | "partialDisabilityFormula"
  | "partialDisability"
  | "earningsTest"
  | "portability"
  | "rehabilitativeIncentiveBenefit"
  | "recurrentDisability"
  | "w2Prep"
  | "claimsIntake"
  | "statutoryDiIntegration"
  | "pflIntegration"
>;

const STD_EXTENDED_FIELD_KEYS: (keyof StdExtendedGridFields)[] = [
  "eligibilityHours",
  "minimumBenefitType",
  "minimumBenefitAmount",
  "firstDayHospital",
  "partialDisabilityFormula",
  "partialDisability",
  "earningsTest",
  "portability",
  "rehabilitativeIncentiveBenefit",
  "recurrentDisability",
  "w2Prep",
  "claimsIntake",
  "statutoryDiIntegration",
  "pflIntegration",
];

// Extended (non-v5) fields — CD-2068. Split out of
// transformStdBenefitsFromApi to keep that function's complexity down.
// Grid and API field names match 1:1, so a single loop over the shared key
// list replaces one `?? undefined` assignment per field. Every key is
// present (as undefined) even when `extended` itself is absent, so a later
// edit to any other field doesn't leave a stale extended value in the grid
// row from a previous plan.
const transformStdExtendedFromApi = (
  extended: StdExtendedBenefitApi | undefined,
): StdExtendedGridFields =>
  Object.fromEntries(
    STD_EXTENDED_FIELD_KEYS.map((key) => [key, extended?.[key] ?? undefined]),
  ) as StdExtendedGridFields;

/**
 * Grid keys whose values persist under `benefits.extended` rather than in the
 * v5 body. PPX uses this to decide which cells of the RFP-requested plan an
 * analyst may edit in the Quotes side-by-side (CD-2301).
 */
export const STD_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> = new Set(
  STD_EXTENDED_FIELD_KEYS,
);
